import axios from "axios";
import { DataTable } from "simple-datatables";
import { showAlert, showConfirm, handleRequestError } from "../utils/alert";
import VMasker from "vanilla-masker";

export default () => ({
  // Authorization State
  hasAccessModule: true,

  isModalOpen: false,
  isSubmitting: false,
  isConsultingCnpj: false,

  editMode: false,
  editId: null,

  suppliersList: [],

  form: {
    name: "",
    trade_name: "",
    document: "",
    email: "",
    phone: "",
    zip_code: "",
    street: "",
    number: "",
    complement: "",
    neighborhood: "",
    city: "",
    state: "",
  },

  dataTableInstance: null,

  async initSuppliers() {
    const token = localStorage.getItem("jwt_token");
    if (!token) {
      window.location.href = "/client/login";
      return;
    }

    window.axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;

    try {
      // Proceed with normal initialization
      await this.loadTable();
    } catch (error) {
      // Captura o erro 403 e bloqueia a view
      if (error.response && error.response.status === 403) {
        this.hasAccessModule = false;
        return;
      }

      // Ignora se for problema de autenticação/pagamento (tratado pelo interceptor global)
      if (error.response && [401, 402].includes(error.response.status)) return;

      console.error("Infrastructure error:", error);
    }
  },

  // ================= MASKS & FORMATTERS =================
  applyDocumentMask(value) {
    if (!value) return "";
    const clean = value.toString().replace(/\D/g, "");
    return clean.length > 11
      ? VMasker.toPattern(clean, "99.999.999/9999-99")
      : VMasker.toPattern(clean, "999.999.999-99");
  },

  applyPhoneMask(value) {
    if (!value) return "";
    const clean = value.toString().replace(/\D/g, "");
    // Dynamically choose mask based on length: Landline (10 digits) vs Mobile (11 digits)
    return clean.length > 10
      ? VMasker.toPattern(clean, "(99) 99999-9999")
      : VMasker.toPattern(clean, "(99) 9999-9999");
  },

  applyCepMask(value) {
    if (!value) return "";
    const clean = value.toString().replace(/\D/g, "");
    return VMasker.toPattern(clean, "99999-993");
  },

  handleDocumentInput(event) {
    this.form.document = this.applyDocumentMask(event.target.value);
  },

  handlePhoneInput(event) {
    this.form.phone = this.applyPhoneMask(event.target.value);
  },

  handleCepInput(event) {
    this.form.zip_code = this.applyCepMask(event.target.value);
  },

  // ================= BRASIL API INTEGRATION =================
  async checkCnpj() {
    const cleanCnpj = this.form.document.replace(/\D/g, "");

    if (cleanCnpj.length !== 14) {
      showAlert(
        "Por favor, digite um CNPJ completo (14 dígitos) para realizar a consulta.",
        "warning",
      );
      return;
    }

    this.isConsultingCnpj = true;

    try {
      // Direct call to Brasil API via client-side fetch/axios
      const response = await axios.get(
        `https://brasilapi.com.br/api/cnpj/v1/${cleanCnpj}`,
      );
      const data = response.data;

      // Autocomplete form fields based on Brazil API schema mapping
      this.form.name = data.razao_social || "";
      this.form.trade_name = data.nome_fantasia || "";
      this.form.zip_code = this.applyCepMask(data.cep || "");
      this.form.street = data.logradouro || "";
      this.form.neighborhood = data.bairro || "";
      this.form.city = data.municipio || "";
      this.form.state = data.uf || "";

      showAlert("Dados do CNPJ importados com sucesso!", "success");
    } catch (error) {
      console.error(error);
      showAlert(
        "Não foi possível localizar o CNPJ informado na Brasil API. Verifique o número e digite manualmente se necessário.",
        "warning",
      );
    } finally {
      this.isConsultingCnpj = false;
    }
  },

  // ================= DATA TABLE =================
  async loadTable() {
    try {
      const response = await axios.get("/api/client/financial/suppliers");
      this.suppliersList = response.data.data;

      if (this.dataTableInstance) {
        this.dataTableInstance.destroy();
      }

      const tbody = document.querySelector("#suppliersTable tbody");
      tbody.innerHTML = this.suppliersList
        .map((supplier) => {
          const docDisplay = this.applyDocumentMask(supplier.document);
          const location =
            supplier.city && supplier.state
              ? `${supplier.city} - ${supplier.state}`
              : "-";

          return `
            <tr class="hover:bg-slate-50/50 transition-colors">
              <td class="py-4 px-4 xl:px-6">
                <p class="font-bold text-slate-800">${supplier.name}</p>
                <p class="text-xs text-slate-500">${supplier.trade_name || ""}</p>
              </td>
              <td class="py-4 px-4 xl:px-6 font-mono text-slate-700 text-sm">
                ${docDisplay}
              </td>
              <td class="py-4 px-4 xl:px-6 text-slate-600 text-sm">
                ${location}
              </td>
              <td class="text-left py-4 px-4 xl:px-6">
                <div class="flex justify-start gap-2">
                  <button
                    onclick="window.dispatchEvent(new CustomEvent('edit-supplier', {detail: ${supplier.id}}))"
                    class="inline-flex items-center justify-center rounded-md bg-orange-500 px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-orange-600 focus:outline-none"
                  >
                    Editar
                  </button>
                  <button
                    onclick="window.dispatchEvent(new CustomEvent('delete-supplier', {detail: ${supplier.id}}))"
                    class="inline-flex items-center justify-center rounded-md bg-red-600 px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-red-700 focus:outline-none"
                  >
                    Excluir
                  </button>
                </div>
              </td>
            </tr>
          `;
        })
        .join("");

      this.dataTableInstance = new DataTable("#suppliersTable", {
        searchable: false,
        perPageSelect: false,
        labels: {
          info: "Mostrando de {start} até {end} de {rows} fornecedores",
          noRows: "Nenhum fornecedor cadastrado",
          searchTitle: "Buscar...",
        },
      });
    } catch (error) {
      console.error(error);
    }
  },

  // ================= MODAL HANDLERS =================
  openCreate() {
    this.editMode = false;
    this.form = {
      name: "",
      trade_name: "",
      document: "",
      email: "",
      phone: "",
      zip_code: "",
      street: "",
      number: "",
      complement: "",
      neighborhood: "",
      city: "",
      state: "",
    };
    this.isModalOpen = true;
  },

  openEdit(id) {
    this.editMode = true;
    this.editId = id;
    const supplier = this.suppliersList.find((s) => s.id === id);

    this.form = {
      name: supplier.name,
      trade_name: supplier.trade_name || "",
      document: this.applyDocumentMask(supplier.document),
      email: supplier.email || "",
      phone: this.applyPhoneMask(supplier.phone),
      zip_code: this.applyCepMask(supplier.zip_code),
      street: supplier.street || "",
      number: supplier.number || "",
      complement: supplier.complement || "",
      neighborhood: supplier.neighborhood || "",
      city: supplier.city || "",
      state: supplier.state || "",
    };
    this.isModalOpen = true;
  },

  // ================= SUBMIT =================
  async submitForm() {
    // Custom Validations
    if (!this.form.document || this.form.document.trim() === "") {
      showAlert("Por favor, preencha o campo de CPF/CNPJ.", "warning");
      return;
    }

    if (!this.form.name || this.form.name.trim() === "") {
      showAlert(
        "Por favor, preencha o Nome ou Razão Social do Fornecedor.",
        "warning",
      );
      return;
    }

    this.isSubmitting = true;

    try {
      if (this.editMode) {
        await axios.put(
          `/api/client/financial/suppliers/${this.editId}`,
          this.form,
        );
        showAlert("Fornecedor atualizado com sucesso!", "success");
      } else {
        await axios.post("/api/client/financial/suppliers", this.form);
        showAlert("Fornecedor cadastrado com sucesso!", "success");
      }

      await this.loadTable();
      this.isModalOpen = false;
    } catch (error) {
      if (
        error.response &&
        error.response.status === 422 &&
        error.response.data.error
      ) {
        showAlert(error.response.data.error, "warning");
      } else {
        handleRequestError(error, "Erro ao guardar o fornecedor.");
      }
    } finally {
      this.isSubmitting = false;
    }
  },

  // ================= DELETE =================
  async deleteRecord(id) {
    const result = await showConfirm(
      "Excluir Fornecedor",
      "Tem certeza? O cadastro será movido para a lixeira, mas o histórico contábil de despesas antigas atreladas a ele será rigorosamente preservado.",
    );

    if (result.isConfirmed) {
      try {
        await axios.delete(`/api/client/financial/suppliers/${id}`);
        await this.loadTable();
        showAlert("Fornecedor removido com sucesso.", "success");
      } catch (error) {
        handleRequestError(error, "Erro ao excluir o fornecedor.");
      }
    }
  },
});
